import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, ChevronDown, Mic, Square, Play, Pause, SmilePlus } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useSettings } from "../context/SettingsContext";
const TEAL = "#0D9488";
const REACTIONS = ["🔥", "💪", "❤️", "👍", "🎉", "😂"];

/** In-app chat thread between a client and their verified coach.
 *  Props:
 *    - shareId (required): the coach_share id used as thread key
 *    - counterpartyLabel: display name of the other side
 *    - collapsed: initial state (default: collapsed on client side, open on coach side)
 *    - onUnreadChange(count): notify parent so it can bubble a nav badge
 */
export default function CoachChatPanel({ shareId, counterpartyLabel, defaultOpen = false, onUnreadChange }) {
  const { settings } = useSettings();
  // Master AI switch AND per-feature ai_coach flag — hide voice-note buttons if either is off.
  const masterOn = (settings?.feature_flags?.ai_features ?? true) !== false;
  const aiVoiceEnabled = masterOn && ((settings?.feature_flags?.ai_coach ?? true) !== false);
  const [open, setOpen] = useState(defaultOpen);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [revoked, setRevoked] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioDataUrl, setAudioDataUrl] = useState(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const scrollRef = useRef(null);

  const load = () => {
    if (!shareId) return;
    setLoading(true);
    api.get(`/coach/threads/${shareId}/messages`)
      .then(({ data }) => { setMessages(Array.isArray(data) ? data : []); setRevoked(false); })
      .catch((err) => {
        // 404 means the share was revoked — hide the panel instead of erroring.
        if (err?.response?.status === 404) setRevoked(true);
        setMessages([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    load();
    // Mark this thread read once the user opens it
    api.post(`/coach/threads/${shareId}/read`).then(() => onUnreadChange?.(0)).catch(() => {});
    // Poll every 15s while open
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shareId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!draft.trim() && !audioDataUrl) return;
    setSending(true);
    try {
      const payload = {};
      if (draft.trim()) payload.body = draft.trim();
      if (audioDataUrl) payload.audio_data_url = audioDataUrl;
      const { data } = await api.post(`/coach/threads/${shareId}/messages`, payload);
      setMessages((prev) => [...prev, data]);
      setDraft("");
      setAudioBlob(null);
      setAudioDataUrl(null);
      setRecordSeconds(0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  // ---------- Voice recording ----------
  const startRec = async () => {
    if (!navigator.mediaDevices || typeof MediaRecorder === "undefined") {
      toast.error("Voice recording isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recordChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordChunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recordChunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 900_000) {
          toast.error("Voice note too long — please keep it under ~60 seconds.");
          return;
        }
        setAudioBlob(blob);
        const reader = new FileReader();
        reader.onloadend = () => setAudioDataUrl(reader.result);
        reader.readAsDataURL(blob);
      };
      rec.start();
      mediaRecRef.current = rec;
      setRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => {
        setRecordSeconds((s) => {
          if (s >= 60) { stopRec(); return s; }
          return s + 1;
        });
      }, 1000);
    } catch (err) {
      toast.error("Microphone access denied.");
    }
  };

  const stopRec = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
    if (recordTimerRef.current) { clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
    setRecording(false);
  };

  const clearAudio = () => {
    setAudioBlob(null);
    setAudioDataUrl(null);
    setRecordSeconds(0);
  };

  useEffect(() => () => {
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") mediaRecRef.current.stop();
  }, []);

  // ---------- Reactions ----------
  const [pickerOpenId, setPickerOpenId] = useState(null);

  const react = async (msgId, emoji) => {
    try {
      const { data } = await api.post(`/coach/threads/${shareId}/messages/${msgId}/reactions`, { emoji });
      setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, reactions: data.reactions } : m));
      setPickerOpenId(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="coach-chat-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 flex items-center gap-2 hover:bg-slate-50 transition-colors border-b border-slate-100"
        data-testid="coach-chat-toggle"
      >
        <MessageCircle className="w-4 h-4" style={{ color: TEAL }} />
        <span className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Chat</span>
        <span className="text-sm font-semibold text-slate-900 ml-1">{counterpartyLabel || "Thread"}</span>
        {revoked && (
          <span className="ml-2 text-[10px] uppercase tracking-widest font-semibold text-rose-600" data-testid="coach-chat-revoked">Thread closed</span>
        )}
        <ChevronDown className={`w-4 h-4 ml-auto text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && !revoked && (
        <div>
          <div
            ref={scrollRef}
            className="max-h-72 overflow-y-auto px-5 py-4 space-y-2 bg-slate-50/40"
            data-testid="coach-chat-list"
          >
            {loading && messages.length === 0 ? (
              <div className="text-xs text-slate-500 italic text-center py-6">Loading messages…</div>
            ) : messages.length === 0 ? (
              <div className="text-xs text-slate-500 italic text-center py-6">
                No messages yet — send the first check-in below.
              </div>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.is_mine ? "justify-end" : "justify-start"} group`}
                  data-testid={`coach-chat-msg-${m.id}`}
                >
                  <div className={`max-w-[75%] flex ${m.is_mine ? "flex-row-reverse" : "flex-row"} items-end gap-1.5`}>
                    <div
                      className={`px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${
                        m.is_mine
                          ? "bg-[#0D9488] text-white rounded-br-sm"
                          : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"
                      }`}
                    >
                      {m.body}
                      {m.audio_data_url && (
                        <audio
                          controls
                          src={m.audio_data_url}
                          className="mt-1 w-full max-w-[240px] h-8"
                          data-testid={`coach-chat-audio-${m.id}`}
                        />
                      )}
                      {m.audio_transcript && (
                        <div
                          className={`mt-1 text-[11px] italic leading-relaxed ${m.is_mine ? "text-white/85" : "text-slate-600"}`}
                          data-testid={`coach-chat-transcript-${m.id}`}
                        >
                          “{m.audio_transcript}”
                        </div>
                      )}
                      <div className={`text-[10px] font-mono-num mt-1 ${m.is_mine ? "text-white/70" : "text-slate-400"}`}>
                        {(m.created_at || "").slice(11, 16)}
                      </div>
                      {/* Existing reaction badges */}
                      {m.reactions && Object.keys(m.reactions).length > 0 && (
                        <div className="flex gap-1 mt-1.5 flex-wrap" data-testid={`coach-chat-reactions-${m.id}`}>
                          {Object.entries(m.reactions).map(([emoji, info]) => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => react(m.id, emoji)}
                              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] border transition-colors ${
                                info.mine
                                  ? "bg-amber-50 border-amber-200 text-amber-800"
                                  : (m.is_mine ? "bg-white/20 border-white/30 text-white" : "bg-slate-50 border-slate-200 text-slate-700")
                              }`}
                              data-testid={`coach-chat-reaction-${m.id}-${emoji}`}
                            >
                              <span>{emoji}</span>
                              <span className="font-mono-num tabular-nums">{info.count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Reaction picker trigger */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setPickerOpenId(pickerOpenId === m.id ? null : m.id)}
                        className="w-7 h-7 rounded-full border border-slate-200 bg-white text-slate-400 hover:text-teal-700 hover:border-teal-300 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                        data-testid={`coach-chat-react-open-${m.id}`}
                        aria-label="React to message"
                      >
                        <SmilePlus className="w-3.5 h-3.5" />
                      </button>
                      {pickerOpenId === m.id && (
                        <div
                          className={`absolute bottom-full mb-1 flex items-center gap-1 bg-white border border-slate-200 rounded-full shadow-md px-1.5 py-1 z-10 ${m.is_mine ? "right-0" : "left-0"}`}
                          data-testid={`coach-chat-react-picker-${m.id}`}
                        >
                          {REACTIONS.map((e) => (
                            <button
                              key={e}
                              type="button"
                              onClick={() => react(m.id, e)}
                              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-50 text-base"
                              data-testid={`coach-chat-react-${m.id}-${e}`}
                              aria-label={`React with ${e}`}
                            >
                              {e}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <form onSubmit={send} className="px-5 py-3 border-t border-slate-100">
            {audioDataUrl && (
              <div className="mb-2 flex items-center gap-2 border border-teal-200 rounded-md p-2 bg-teal-50" data-testid="coach-chat-audio-preview">
                <audio controls src={audioDataUrl} className="flex-1 h-8" />
                <button
                  type="button"
                  onClick={clearAudio}
                  className="text-[10px] uppercase tracking-widest font-semibold text-rose-600 hover:text-rose-800"
                  data-testid="coach-chat-audio-clear"
                >
                  Remove
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, 2000))}
                placeholder={recording ? "Recording…" : (audioDataUrl ? "Add a note (optional)…" : "Type a quick check-in…")}
                rows={1}
                disabled={recording}
                className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm resize-none min-h-[36px]"
                data-testid="coach-chat-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(e);
                  }
                }}
              />
              {aiVoiceEnabled && (recording ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={stopRec}
                  className="text-white shrink-0 bg-rose-600 hover:bg-rose-700"
                  data-testid="coach-chat-mic-stop"
                >
                  <Square className="w-3.5 h-3.5 mr-1 fill-current" />
                  {recordSeconds}s
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={startRec}
                  className="text-teal-700 hover:bg-teal-50 shrink-0"
                  data-testid="coach-chat-mic"
                  title="Record a voice memo (≤ 60s)"
                >
                  <Mic className="w-4 h-4" />
                </Button>
              ))}
              <Button
                type="submit"
                size="sm"
                disabled={(!draft.trim() && !audioDataUrl) || sending}
                className="text-white shrink-0"
                style={{ background: TEAL }}
                data-testid="coach-chat-send"
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
